import * as Yup from 'yup';
import { Box, Checkbox, FormControlLabel, Grid, TextField, Typography } from '@material-ui/core';
import { useFormik } from 'formik';
import { useHistory } from 'react-router-dom';
import { useTheme } from '@material-ui/core/styles';
import arrowRight from '_assets/login/arrow-right.svg';
import clsx from 'clsx';
import DefaultButton from 'components/DefaultButton';
import googleIcon from '_assets/login/google_icon.svg';
import routes from 'config/routes';
import separator from '_assets/parameters/separator.svg';
import userIcon from '_assets/login/user_icon.svg';
import useStyles from './styles';

const initialValues = {
  email: '',
  password: '',
  remember: false,
};

const validationSchema = Yup.object({
  email: Yup.string().email('Veuillez saisir une adresse mail valide').required('Champ obligatoire'),
  password: Yup.string().required('Champ obligatoire'),
});

const CustomIcon = ({ color, image }) => {
  const theme = useTheme();
  const bgColor = color ?? theme.palette.primary.main;

  return (
    <Box
      style={{
        backgroundColor: bgColor,
        WebkitMaskSize: 'contain',
        maskPosition: 'center',
        WebkitMaskPosition: 'center',
        maskSize: 'contain',
        WebkitMaskRepeat: 'no-repeat',
        MaskRepeat: 'no-repeat',
        WebkitMaskImage: `url(${image})`,
        maskImage: `url(${image})`,
        width: '25px',
        height: '25px',
      }}
    />
  );
};

function LoginForm({ errorMessage, onForgotPasswordClick, onGoogleClick, onValidate }) {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();

  const { errors, handleChange, handleSubmit, isSubmitting, touched, values } = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      onValidate(values.email, values.password, values.remember);
    },
  });

  return (
    <form className={classes.formLogin} onSubmit={handleSubmit}>
      <Grid container item direction="column" xs={6} alignItems="center" style={{ paddingLeft: theme.spacing(6) }}>
        <Typography variant="h3" style={{ fontFamily: 'Roboto', margin: '48px 0' }}>
          Nouveau sur Maia Partners ?
        </Typography>

        <DefaultButton
          className={classes.leftButton}
          customColor="primary"
          variant="outlined-custom"
          onClick={() => history.push(routes.signup.root)}
        >
          <Typography style={{ margin: 'auto' }}>Créer un compte</Typography>
          <CustomIcon color={theme.palette.primary.main} image={arrowRight} />
        </DefaultButton>

        <DefaultButton
          className={classes.leftButton}
          customColor="tertiary"
          variant="outlined-custom"
          onClick={() => onGoogleClick(false)}
        >
          <Typography style={{ margin: 'auto' }}>Créer un compte avec Google</Typography>
          <CustomIcon color={theme.palette.tertiary.main} image={arrowRight} />
        </DefaultButton>
      </Grid>

      <Grid container item xs={1} justifyContent="center">
        <img src={separator} alt="separator" />
      </Grid>

      <Grid container item xs={6} direction="column" alignItems="center" style={{ paddingRight: theme.spacing(6) }}>
        <Typography variant="h3" style={{ fontFamily: 'Roboto', margin: '48px 0' }}>
          Vous possédez un compte ?
        </Typography>

        <TextField
          className={classes.textField}
          fullWidth
          id="email"
          name="email"
          label="Adresse email"
          variant="outlined"
          value={values.email}
          onChange={handleChange}
          error={touched.email && Boolean(errors.email)}
          helperText={touched.email && errors.email}
        />

        <TextField
          fullWidth
          id="password"
          name="password"
          label="Mot de passe"
          type="password"
          variant="outlined"
          value={values.password}
          onChange={handleChange}
          error={touched.password && Boolean(errors.password)}
          helperText={touched.password && errors.password}
        />

        {errorMessage && (
          <Typography color="error" style={{ marginLeft: '8px', marginTop: '10px' }}>
            {errorMessage}
          </Typography>
        )}

        <Box style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <FormControlLabel
            control={<Checkbox color="primary" name="remember" checked={values.remember} onChange={handleChange} />}
            label={'Se souvenir de moi'}
          />

          <Typography className={classes.link} onClick={onForgotPasswordClick}>
            Mot de passe oublié ?
          </Typography>
        </Box>

        <Box className={classes.loginContainer}>
          <DefaultButton
            className={classes.rightButton}
            color="secondary"
            disabled={isSubmitting}
            type="submit"
            variant="contained"
          >
            <CustomIcon color={theme.palette.common.white} image={userIcon} />
            <Typography style={{ margin: 'auto' }}>Se connecter</Typography>
          </DefaultButton>

          <DefaultButton
            className={clsx(classes.rightButton, classes.googleButton)}
            disabled={isSubmitting}
            variant="contained"
            onClick={() => onGoogleClick(values.remember)}
          >
            <CustomIcon color={theme.palette.common.white} image={googleIcon} />
            <Typography style={{ margin: 'auto' }}>Se connecter via Google</Typography>
          </DefaultButton>
        </Box>
      </Grid>
    </form>
  );
}

export default LoginForm;
